import { createContext } from "react";

export type GlassGroupStore = {
  readonly root: HTMLElement | null;
  readonly active: boolean;
  members: Set<HTMLElement>;
  schedule: () => void;
  subscribe: (listener: () => void) => () => void;
  emit: () => void;
  register: (element: HTMLElement) => () => void;
  contains: (element: HTMLElement | null) => boolean;
  setRoot: (element: HTMLElement | null) => void;
  setActive: (value: boolean, elements?: readonly HTMLElement[]) => void;
  setSchedule: (callback: () => void) => void;
};

export function createGlassGroupStore(): GlassGroupStore {
  const listeners = new Set<() => void>();
  let root: HTMLElement | null = null;
  let active = false;
  let surfaceMembers = new Set<HTMLElement>();
  let schedule = () => {};
  const store: GlassGroupStore = {
    get root() { return root; },
    get active() { return active; },
    members: new Set(),
    schedule: () => { schedule(); },
    setRoot(element) { root = element; },
    setActive(value, elements = []) {
      const next = new Set(value ? elements : []);
      if (active === value && next.size === surfaceMembers.size &&
        [...next].every((element) => surfaceMembers.has(element))) return;
      active = value;
      surfaceMembers = next;
      store.emit();
    },
    setSchedule(callback) { schedule = callback; },
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    emit() { for (const listener of listeners) listener(); },
    register(element) {
      store.members.add(element);
      store.emit();
      store.schedule();
      return () => {
        store.members.delete(element);
        store.emit();
        store.schedule();
      };
    },
    contains(element) {
      return Boolean(store.active && element && store.members.has(element) && surfaceMembers.has(element) &&
        store.root?.contains(element) &&
        element.closest(".ark-liquid-group") === store.root);
    },
  };
  return store;
}

export const LiquidGlassGroupContext = createContext<GlassGroupStore | null>(null);
export const subscribeWithoutGroup = () => () => {};
